import React from "react"
import styled, { css } from "styled-components"
import colors from "@constants/colors"
import DesktopSlideout from "@components/Layout/DesktopSlideout"
import { productQuery } from "@views/ProductDetails/graphql"
import { allPathsCarsQuery } from "@views/Cars/graphql"
import requestStaticProps from "@utils/requestStaticProps"
import useGtmEvents from "@hooks/useGtmEvents"
import { formatCurrency } from "@utils/numbers"
import { getBlogUrl, getGalleryUrl, getInstallationUrl } from "@utils/urls"
import PlainButton from "@components/buttons/PlainButton"
import { LaptopUpOnly, MobileOnly } from "@utils/MediaQueries"
import _, { filter } from "lodash"
import MarkdownIt from "markdown-it"
import VerticalSpace from "@components/VerticalSpace"
import OptionGroup from "@views/ProductDetails/components/OptionGroup"
import Accordion from "@components/Accordion"
import Button from "@components/buttons/Button"
import {
  body,
  bodyBold,
  smallcaps,
  h5,
  subtextBold,
} from "@constants/typography"
import { up, down } from "styled-breakpoints"
import Link from "next/link"
import { columns } from "@constants/layout"
import { IconWrapper } from "@components/icons/utils"
import { Menu, Close } from "@components/icons"
import { Image, ResponsiveImageType } from "react-datocms"

const Markdown = new MarkdownIt()

const DEFAULT_OPTION_TITLE = "Title"
const DEFAULT_OPTION_VALUE = "Default Title"

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 40px 20px 80px;

  ${up("tablet")} {
    padding: 40px;
  }

  ${down("tablet")} {
    max-width: 100vw;
    margin: auto;
    margin-right: 0;
  }
`
const ImageWrapper = styled.div`
  padding: 36px;
  max-width: 400px;
  max-height: 400px;
  margin: 20px auto;

  .DatoImage {
    position: relative;
    width: 100%;
    height: 100%;
    padding: 36px;
  }

  ${down("tablet")} {
    max-width: 80vw;
    max-height: 300px;
  }

  ${up("tablet")} {
    padding: 0;

    .DatoImage {
      padding: 0;

      div {
        position: initial !important;
        top: unset !important;
        left: unset !important;
        right: unset !important;
        bottom: unset !important;
        padding-bottom: 66%;
      }

      img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: top center;
      }
    }
  }
`

const Name = styled.div`
  ${h5}
  color: ${colors.obsidian};
`

const Sku = styled.div`
  ${smallcaps}
  color: ${colors.obsidian};
`

const Prices = styled.div`
  display: flex;
`

const Price = styled.div`
  ${h5}
  font-size: 18px;
  line-height: 32px;
  color: ${colors.primary};
`

const CompareAtPrice = styled.div`
  ${h5}
  margin-right: 6px;
  font-size: 18px;
  line-height: 32px;
  text-decoration: line-through;
  color: ${colors.slateDark};
`

const Description = styled.div`
  ${body}
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${colors.slateDark};

  img {
    display: block;
  }

  h6,
  p,
  ul,
  li {
    margin: 0;
    padding: 0;
  }

  h6 {
    ${bodyBold}
    margin-bottom: 16px;
    color: ${colors.obsidian};
  }

  p {
    margin-bottom: 16px;
  }

  ul {
    margin-bottom: 16px;
    list-style: none;
  }

  li {
    padding-left: 0.5em;
  }

  li:before {
    content: "-";
    display: inline-block;
    transform: translateX(-0.5em);
  }
`

const LinkText = styled.div`
  ${subtextBold}
  width: fit-content;
  margin-bottom: 2px;
  text-decoration: underline;

  &:hover {
    cursor: pointer;
  }
`

const WarningLink = styled.a`
  ${body};
  width: initial;
  width: max-content;
  font-weight: 500;
  color: ${colors.obsidian};

  ${up("laptop")} {
    &:hover {
      cursor: pointer;
    }
  }
`
const Variant = styled.button<{ isSelected: boolean }>`
  ${({ isSelected }) => css`
    align-items: center;
    justify-content: space-between;
    display: flex;
    width: 100%;
    margin-bottom: 16px;
    padding: 20px;
    background-color: transparent;
    outline: none;
    border: ${isSelected
      ? `solid 2px ${colors.obsidian}`
      : `solid 1px ${colors.divider}`};
    border-radius: 4px;

    &:hover {
      cursor: pointer;
    }
  `}

  ${Name} {
    ${bodyBold}
    text-align: left;
    color: ${colors.slateDark};
  }

  ${Price} {
    ${body}
    flex-shrink: 0;
    margin-left: 24px;
    color: ${colors.slateDark};
  }
`

const CloseWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 20px;
  display: inline-block;
  z-index: 9999;
  ${down("tablet")} {
    top: 20px;
    right: 20px;
  }

  ${IconWrapper} {
    transform: rotate(90deg);
    width: 32px;
    height: 32px;
    cursor: pointer;

    ${up("tablet")} {
      display: flex;
      transform: rotate(0);
      width: 40px;
      height: 40px;
    }
  }
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.divider};
`

const Affirm = styled.p`
  ${body}
  color: ${colors.slateDark};
  .affirm-ala-price {
    ${bodyBold}
  }
  a {
    text-decoration: underline;
  }
`
const BracketBtnContainer = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100px;
  padding: 20px 15px 15px 15px;
  background-color: white;

  @media (max-width: 500px) {
    width: 100vw !important;
  }
`

const ProductSlideout: any = ({
  params,
  productOptions = [],
  filterData,
  isOpen,
  onRequestClose,
  onSelect,
  openProductDetails,
  BuildPanelBtn,
  CTA = "Add to order",
}) => {
  // console.log(params)
  if (!params) {
    return null
  }

  const [data, setData] = React.useState<any>(undefined)

  const [options, setOptions] = React.useState<any>({})
  const [targetOption, setTargetOpt] = React.useState<string>("")
  const [filteredOptions, setFilteredOptions] = React.useState<any>([])
  const [availableOptions, setAvailableOptions] = React.useState<any>({})
  const [selectedVariant, setSelectedVariant] = React.useState<any>(null)
  const fallbackImage =
    data?.collectionsConfiguration?.defaultProductCardImage?.responsiveImage
      ?.src

  React.useEffect(() => {
    const variants = variantData

    if (variants.length === 1) {
      setSelectedVariant(variants[0])
    }
    setDefaultValues({}, groupedByOptionType)
  }, [data])

  const [variantData, setVariantData] = React.useState<any>([])
  const [defaultVariant, setDefaultVariant] = React.useState<any>(undefined)
  const [allOptions, setAllOptions] = React.useState<any>([])
  const [overridables, setOverridables] = React.useState<any>({})
  const [groupedByOptionType, setGroupedByOptionType] = React.useState<any>({})
  const [hideOptions, setHideOptions] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (!params) {
      return () => {
        // this now gets called when the component unmounts
      }
    }

    ;(async () => {
      // console.log("Loading data")
      // console.log(params)
      const { data, error } = params.product?.slug
        ? await requestStaticProps(productQuery, { slug: params.product.slug })
        : { data: {}, error: undefined }

      if (error) {
        // console.error(error)
      } else {
        // console.log(data)
        setData(data)

        const variantData =
          data?.product?.variantsJson || data?.product?.variants || []

        setVariantData(variantData)
        setDefaultVariant(variantData[0])

        const allOptions = _.uniqBy(
          _.flatten(variantData.map(({ options }) => options)) as Array<any>,
          ({ optionType, optionValue }) => {
            return `${optionType.name} - ${optionValue}`
          }
        ) as any

        setHideOptions(
          allOptions.length === 1 &&
            allOptions[0].optionType?.name === DEFAULT_OPTION_TITLE &&
            allOptions[0].optionValue === DEFAULT_OPTION_VALUE
        )

        let groupedByOptionType = _.groupBy(
          allOptions,
          ({ optionType }) => optionType.name
        )

        //Reorder if bracket option is present
        if (groupedByOptionType["Bracket"]) {
          const groupOrder = {
            Make: [],
            Model: [],
            Bracket: [],
          }

          //Put Brackets last
          Object.assign(groupOrder, groupedByOptionType)
          groupedByOptionType = groupOrder
        }

        if (groupedByOptionType["Material"]) {
          const groupOrder = {
            Material: [],
          }

          //Put Brackets last
          Object.assign(groupOrder, groupedByOptionType)
          groupedByOptionType = groupOrder
        }

        if (groupedByOptionType["Year"]) {
          groupedByOptionType["Year"] = groupedByOptionType["Year"].sort(
            (a, b) => String(b.optionValue).localeCompare(String(a.optionValue))
          )
        }

        let overridables = {}
        productOptions.map((option: any) => {
          if (option.overridable) {
            if (groupedByOptionType[option.label]) {
              overridables[option.label] = groupedByOptionType[option.label]
            } else {
              overridables[option.label] = option.options
            }
          }
        })

        setOverridables(overridables)

        setAllOptions(allOptions)
        setGroupedByOptionType(groupedByOptionType)

        // console.log(allOptions)

        //Correct Chevy and GMC
        if (filterData?.Make == "Chevy") {
          filterData.Make = "Chevy-GMC"
        }

        if (filterData?.Make == "Dodge") {
          filterData.Make = "Dodge-Plymouth"
        }

        setOptions(filterData)

        const filterResults = applyFilters(
          filterData,
          variantData,
          groupedByOptionType
        )

        // console.log(filterData)
        // console.log(filterResults)

        const filteredVariants = filterResults.filteredVariants
        const filteredOptions = filterResults.availableOptions

        if (filteredVariants.length === 1) {
          setDefaultValues(filterData, filteredOptions)
          setSelectedVariant(filteredVariants[0])
          return
        }
      }
    })()

    return () => {
      // this now gets called when the component unmounts
    }
  }, [params, filterData])

  //Facilitates expansion of target option and scrolls to focus
  const setTargetOption = (option) => {
    if (option == "") {
      try {
        document
          .getElementById("option-" + targetOption)
          ?.scrollIntoView({ behavior: "smooth", block: "center" })
      } catch {}
    }

    setTargetOpt(option)
  }

  const onSelectOptionValue = (type, value) => {
    //Set value
    const filters = { ...options, [type]: value }

    //Unselect function
    if (options[type] == value || value.trim() == "") {
      delete filters[type]
    }

    //Clear target option
    setTargetOption("")

    const filterResults = applyFilters(filters)
    const filteredVariants = filterResults.filteredVariants
    const filteredOptions = filterResults.availableOptions

    // console.log(filterResults)

    if (filteredVariants.length === 1) {
      setDefaultValues(filters, filteredOptions)
      setSelectedVariant(filteredVariants[0])
      return
    } else {
      // console.log(filters)
      // console.log(filteredVariants)
      if (filteredVariants.length === 0) {
        const newFilters = resetOptions(filters, [type])
        // console.log(newFilters)
        const newFilteredOptions = applyFilters(newFilters).availableOptions
        setDefaultValues(newFilters, newFilteredOptions)
      } else {
        setDefaultValues(filters, filteredOptions)
      }

      setSelectedVariant(undefined)

      return
    }
  }

  const setDefaultValues = (filters, filteredOptions) => {
    if (!filteredOptions) {
      filteredOptions = groupedByOptionType
    }

    // console.log(filteredOptions)

    Object.keys(groupedByOptionType).forEach((filter) => {
      if (!filters[filter]) {
        if (filteredOptions[filter] && filteredOptions[filter].length == 1) {
          // console.log(
          //   "set default " +
          //     (typeof filteredOptions[filter][0] == "object"
          //       ? filteredOptions[filter][0].optionValue
          //       : filteredOptions[filter][0])
          // )
          if (Object.keys(overridables).length > 0) {
            if (overridables[filter]) {
              filters[filter] =
                typeof filteredOptions[filter][0] == "object"
                  ? filteredOptions[filter][0].optionValue
                  : filteredOptions[filter][0]
            }
          } else {
            filters[filter] =
              typeof filteredOptions[filter][0] == "object"
                ? filteredOptions[filter][0].optionValue
                : filteredOptions[filter][0]
          }
        }
      }
    })

    // console.log(filters)

    setOptions(filters)
  }

  const applyFilters = (filters: any, filterData?, groupedOption?) => {
    // console.log(filters)

    const variants = filterData || variantData
    const groupedByOption = groupedOption || groupedByOptionType

    // console.log(variants)
    // console.log(filters)
    // console.log(filterData)

    // console.log(groupedByOptionType)
    // console.log(groupedOption)

    const filteredVariants = variants.filter((variant) => {
      let include = true

      Object.keys(filters).forEach((filter) => {
        if (groupedByOption[filter]) {
          const thisOption = variant.options.find((option) => {
            return (
              filter === option.optionType.name &&
              filters[filter] === option.optionValue
            )
          })

          if (!thisOption) {
            include = false
          }
        }
      })

      return include
    })

    // console.log(filteredVariants)

    const availableOptions = _.uniqBy(
      _.flatten(filteredVariants.map(({ options }) => options)),
      (option: any) => {
        return `${option?.optionType.name} - ${option?.optionValue}`
      }
    ).reduce((acc: any, curr: any) => {
      if (acc[curr.optionType.name]) {
        acc[curr.optionType.name].push(curr.optionValue)
      } else {
        acc[curr.optionType.name] = [curr.optionValue]
      }
      return acc
    }, {}) as any

    // console.log(availableOptions)

    setFilteredOptions(filteredVariants)
    setAvailableOptions(availableOptions)

    return {
      filteredVariants: filteredVariants,
      availableOptions: availableOptions,
    }
  }

  const resetOptions = (filters, exceptions: [any]) => {
    Object.keys(filters).forEach((filter) => {
      if (exceptions.indexOf(filter) === -1) {
        // console.log("delete " + filter)
        delete filters[filter]
      }
    })

    return filters
  }

  const linkOuts = _.groupBy(data?.product?.linkOut || [], "__typename")

  enum LinkOutRecords {
    Blog = "BlogRecord",
    Gallery = "GalleryRecord",
    InstallationGuide = "InstallationGuideRecord",
  }

  const linkOutHandlerMap = {
    [LinkOutRecords.Blog]: {
      label: "Blog",
      getUrl: getBlogUrl,
    },
    [LinkOutRecords.Gallery]: {
      label: "Gallery",
      getUrl: getGalleryUrl,
    },
    [LinkOutRecords.InstallationGuide]: {
      label: "Installation Guide",
      getUrl: getInstallationUrl,
    },
  }

  const renderLink = (l) => {
    return (
      <Link
        key={l.slug}
        href={linkOutHandlerMap[l.__typename].getUrl(l.slug)}
        passHref
      >
        <LinkText>{l.title}</LinkText>
      </Link>
    )
  }

  const [showSignupForm, setShowSignupForm] = React.useState<boolean>(false)

  const buildOptions = () => {
    if (hideOptions) {
      return <></>
    }

    // console.log(overridables)

    if (Object.keys(overridables).length > 0) {
      return Object.keys(overridables).map((optionType, i) => {
        if (overridables[optionType]) {
          return (
            <OptionGroup
              key={"option-" + i}
              name={optionType}
              value={options[optionType]}
              count={i + 1}
              hideDisabled={
                optionType == "Model" &&
                overridables["Make"] &&
                overridables["Model"]
              }
              optionValues={overridables[optionType]}
              availableOptions={availableOptions[optionType]}
              isExpaned={targetOption == optionType}
              onSelectOption={() => {
                setTargetOption(optionType)
              }}
              onSelectValue={onSelectOptionValue}
            />
          )
        }

        return null
      })
    }

    return Object.keys(groupedByOptionType).map((optionType, i) => {
      // console.log(groupedByOptionType[optionType])
      return (
        <OptionGroup
          key={"option-" + i}
          name={optionType}
          value={options[optionType]}
          count={i + 1}
          optionValues={groupedByOptionType[optionType]}
          availableOptions={availableOptions[optionType]}
          isExpaned={targetOption == optionType}
          onSelectOption={() => {
            setTargetOption(optionType)
          }}
          onSelectValue={onSelectOptionValue}
        />
      )
    })
  }

  const cssProps = css`
    display: block;
  `

  if (!data?.product) {
    return (
      <DesktopSlideout
        width="600px"
        isOpen={isOpen}
        onRequestClose={() => {}}
        cssProps={cssProps}
      >
        <CloseWrapper>
          <IconWrapper>
            <Close
              strokeColor={colors.obsidian}
              width="40px"
              height="40px"
              onClick={onRequestClose}
            />
          </IconWrapper>
        </CloseWrapper>
        <Container>
          <Name>{params?.product?.productName}</Name>
          <VerticalSpace height={8} />
          <Sku>
            SKU{" "}
            {selectedVariant
              ? selectedVariant.variantSku
              : params?.product?.commonSku}
          </Sku>
          {data?.product?.thumbnail ? (
            <>
              <VerticalSpace height={22} mobileHeight={30} />
              <ImageWrapper>
                <Image
                  data={data?.product?.thumbnail.responsiveImage}
                  className="DatoImage"
                />
              </ImageWrapper>
            </>
          ) : (
            ""
          )}
        </Container>
      </DesktopSlideout>
    )
  } else {
    return (
      <DesktopSlideout
        width="600px"
        isOpen={isOpen}
        onRequestClose={() => {}}
        cssProps={cssProps}
      >
        <CloseWrapper>
          <IconWrapper>
            <Close
              strokeColor={colors.obsidian}
              width="40px"
              height="40px"
              onClick={onRequestClose}
            />
          </IconWrapper>
        </CloseWrapper>
        <Container>
          <Name>{data?.product?.productName}</Name>
          <VerticalSpace height={8} />
          <Sku>
            SKU{" "}
            {selectedVariant
              ? selectedVariant.variantSku
              : data?.product?.commonSku}
          </Sku>
          {data?.product?.thumbnail ? (
            <>
              <VerticalSpace height={22} mobileHeight={30} />
              <ImageWrapper>
                <Image
                  data={data?.product?.thumbnail.responsiveImage}
                  className="DatoImage"
                />
              </ImageWrapper>
            </>
          ) : (
            ""
          )}
          <VerticalSpace height={22} mobileHeight={30} />
          <MobileOnly>
            <Prices>
              {selectedVariant && selectedVariant.compareAtPrice && (
                <CompareAtPrice>
                  {formatCurrency(
                    selectedVariant && selectedVariant.compareAtPrice
                      ? selectedVariant.compareAtPrice
                      : undefined,
                    "USD",
                    "en-US"
                  )}
                </CompareAtPrice>
              )}
              <Price>
                {formatCurrency(
                  selectedVariant
                    ? selectedVariant.price
                    : defaultVariant?.price,
                  "USD",
                  "en-US"
                )}
              </Price>
            </Prices>
            <Affirm
              className="affirm-as-low-as"
              data-amount={
                (selectedVariant
                  ? selectedVariant.price
                    ? selectedVariant.price
                    : 0
                  : defaultVariant?.price
                  ? defaultVariant?.price
                  : 0) * 100
              }
              data-page-type="category"
            />
            <VerticalSpace height={26} />
          </MobileOnly>
          <Description
            dangerouslySetInnerHTML={{
              __html: Markdown.render(
                data?.product?.description.replace(/(<([^>]+)>)/gi, "")
              ),
            }}
          />
          <VerticalSpace height={20} />
          <PlainButton onClick={openProductDetails}>
            <IconWrapper>
              <Menu strokeColor={colors.slateMid} />
            </IconWrapper>
            View Product Details
          </PlainButton>
          {Object.keys(linkOuts).length > 0 && (
            <VerticalSpace height={76} mobileHeight={48} />
          )}
          {linkOuts.BlogRecord && (
            <Accordion label="Blogs">
              {linkOuts.BlogRecord.map(renderLink)}
            </Accordion>
          )}
          {linkOuts.GalleryRecord && (
            <Accordion label="Galleries">
              {linkOuts.GalleryRecord.map(renderLink)}
            </Accordion>
          )}
          {linkOuts.InstallationGuideRecord && (
            <Accordion label="Installation Guides">
              {linkOuts.InstallationGuideRecord.map(renderLink)}
            </Accordion>
          )}
          <VerticalSpace height={30} mobileHeight={48} />
          {!hideOptions && Object.keys(linkOuts).length === 0 && (
            <>
              <Divider />
              <VerticalSpace height={30} mobileHeight={48} />
            </>
          )}

          {buildOptions()}

          {Object.keys(overridables).length > 0 ? (
            <>
              <Variant
                isSelected={!selectedVariant}
                onClick={(e) => {
                  setSelectedVariant(undefined)
                }}
              >
                <Name>None</Name>
              </Variant>

              {filteredOptions.map((variant, i) => (
                <Variant
                  key={variant.variantName + "-" + i}
                  isSelected={variant.id === selectedVariant?.id}
                  onClick={(e) => {
                    setSelectedVariant(variant)
                  }}
                >
                  <Name>{variant.variantName}</Name>
                  <Price>{`+ $${Number.parseFloat(
                    data.product.isDiscounted == "yes"
                      ? variant.discountedPrice
                      : (variant.price as any)
                  ).toFixed(2)}`}</Price>
                </Variant>
              ))}
              {BuildPanelBtn ? (
                <>{BuildPanelBtn}</>
              ) : (
                <>
                  <BracketBtnContainer>
                    <Button
                      onClick={(e) => {
                        onSelect(data.product, selectedVariant)
                      }}
                    >
                      {selectedVariant
                        ? "Update - " +
                          formatCurrency(
                            selectedVariant
                              ? selectedVariant.price
                              : defaultVariant?.price,
                            "USD",
                            "en-US"
                          )
                        : "None Selected"}
                    </Button>
                  </BracketBtnContainer>
                </>
              )}
            </>
          ) : (
            <Button
              disabled={!selectedVariant}
              onClick={(e) => {
                onSelect(data.product, selectedVariant, data?.group)
              }}
            >
              {CTA}{" "}
              {selectedVariant
                ? " - " +
                  formatCurrency(
                    selectedVariant
                      ? selectedVariant.price
                      : defaultVariant?.price,
                    "USD",
                    "en-US"
                  )
                : ""}
            </Button>
          )}
          <VerticalSpace height={164} mobileHeight={0} />
        </Container>
      </DesktopSlideout>
    )
  }
}

export default ProductSlideout
